import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MdMenu, MdLock, MdClose } from 'react-icons/md';
import { toggleSidebar } from '../../store/uiSlice';
import { selectUser, selectRole } from '../../store/authSlice';
import { authApi } from '../../api/authApi';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from '../LanguageSwitcher';

const MODULE_LABEL = {
  HEALTHCARE: { label: 'Healthcare', color: 'bg-blue-100 text-blue-700' },
  FACTORY:    { label: 'Factory',    color: 'bg-orange-100 text-orange-700' },
  BILLING:    { label: 'Billing',    color: 'bg-green-100 text-green-700' },
};

function ChangePasswordModal({ onClose }) {
  const [form,    setForm]    = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.newPassword !== form.confirmPassword)
      return setError('New passwords do not match');
    if (form.newPassword.length < 8)
      return setError('New password must be at least 8 characters');
    setSaving(true);
    try {
      await authApi.changePassword({
        currentPassword: form.currentPassword,
        newPassword:     form.newPassword,
      });
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <MdLock size={18} className="text-primary" /> Change Password
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100">
            <MdClose size={18} />
          </button>
        </div>

        {success ? (
          <div className="py-6 text-center text-green-600 font-medium">
            ✓ Password changed successfully!
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            {error && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                {error}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Current Password</label>
              <input
                type="password" required autoFocus
                className="input w-full"
                value={form.currentPassword}
                onChange={e => setForm(f => ({ ...f, currentPassword: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">New Password</label>
              <input
                type="password" required
                className="input w-full"
                placeholder="Min 8 characters"
                value={form.newPassword}
                onChange={e => setForm(f => ({ ...f, newPassword: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Confirm New Password</label>
              <input
                type="password" required
                className="input w-full"
                value={form.confirmPassword}
                onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex items-center gap-1.5">
                <MdLock size={14} /> {saving ? 'Saving…' : 'Update Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Header({ title, module }) {
  const dispatch = useDispatch();
  const user     = useSelector(selectUser);
  const role     = useSelector(selectRole);
  const mod      = MODULE_LABEL[module];
  const [showChangePw, setShowChangePw] = useState(false);
  const [showMenu,     setShowMenu]     = useState(false);

  return (
    <>
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}

      <header className="flex items-center justify-between h-14 px-5 bg-white
                         border-b border-neutral-200 flex-shrink-0">
        {/* Left */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => dispatch(toggleSidebar())}
            className="p-1.5 rounded-lg hover:bg-neutral-100 transition md:hidden"
            aria-label="Toggle menu"
          >
            <MdMenu size={22} />
          </button>
          <h1 className="text-base font-semibold text-neutral-800">{title}</h1>
          {mod && (
            <span className={`hidden sm:inline text-xs px-2 py-0.5 rounded-full font-medium ${mod.color}`}>
              {mod.label}
            </span>
          )}
        </div>

        {/* Right */}
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <NotificationBell />

          {/* User avatar + dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowMenu(v => !v)}
              className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-neutral-100 transition">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center
                             text-white text-sm font-semibold">
                {user?.firstName?.[0]?.toUpperCase()}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-xs font-medium text-neutral-800 leading-none">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-neutral-400 leading-none mt-0.5">
                  {role?.replace('_', ' ')}
                </p>
              </div>
            </button>

            {showMenu && (
              <>
                {/* backdrop */}
                <div className="fixed inset-0 z-30" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-neutral-100 z-40 py-1">
                  <button
                    onClick={() => { setShowMenu(false); setShowChangePw(true); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50">
                    <MdLock size={15} className="text-neutral-400" /> Change Password
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
